import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link } from "wouter";
import { useGetTemplate, useUpdateTemplate, useRenderTemplate, useListTemplates, useGetRepDefaults, useListSnippets, useListTemplateUsedSnippets, useGetTemplateEngagement, getGetTemplateQueryKey, getListTemplatesQueryKey, getListTemplateUsedSnippetsQueryKey, getGetTemplateEngagementQueryKey, type TemplateEngagement } from "@workspace/api-client-react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { useDebounce } from "@/hooks/use-debounce";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ArrowLeft, Send, Save, Info, X, Pencil, Check, Puzzle, Eye, MousePointerClick, BarChart3 } from "lucide-react";
import {
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip as RTooltip,
  XAxis,
  YAxis,
} from "recharts";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { apiErrorMessage } from "@/lib/apiError";
import { isAutoSuffixedSlug, predictSlugAdjustment } from "@/lib/templateSlug";
import { MultiClientPreview } from "@/components/multi-client-preview";

const SLUG_NOTICE_DISMISS_KEY = "mailcraft.slugNotice.dismissed";

// Dismissals are stored as a list of strings under SLUG_NOTICE_DISMISS_KEY.
// Newly-written entries are just the template id (e.g. "42").
// Legacy entries written before this change are "<id>:<slug>" — we treat any
// entry whose prefix before the first colon matches the current template id
// as a valid dismissal so existing dismissals keep working without migration.
function readDismissedSlugNotices(): Set<string> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.localStorage.getItem(SLUG_NOTICE_DISMISS_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return new Set(parsed.map(String));
  } catch {
    /* noop */
  }
  return new Set();
}

function persistDismissedSlugNotices(ids: Set<string>): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(
      SLUG_NOTICE_DISMISS_KEY,
      JSON.stringify(Array.from(ids)),
    );
  } catch {
    /* noop */
  }
}

function isSlugNoticeDismissedForTemplate(
  dismissed: Set<string>,
  templateId: number,
): boolean {
  const idStr = String(templateId);
  if (dismissed.has(idStr)) return true;
  const legacyPrefix = `${idStr}:`;
  for (const entry of dismissed) {
    if (entry.startsWith(legacyPrefix)) return true;
  }
  return false;
}

export default function TemplateDetail() {
  const { id } = useParams();
  const templateId = parseInt(id || "0", 10);
  const queryClient = useQueryClient();

  const { data: template, isLoading } = useGetTemplate(templateId, {
    query: { enabled: !!templateId, queryKey: getGetTemplateQueryKey(templateId) }
  });

  const updateMutation = useUpdateTemplate();
  const renameMutation = useUpdateTemplate();
  const renderMutation = useRenderTemplate();
  const userTemplatesQuery = useListTemplates({ source: "user" });
  // Pull saved Representative name so the live preview's `agentName` field
  // (and any other rep-keyed variable) is auto-filled with the user's
  // chosen rep identity instead of the seeded sample value. Mirrors the
  // same auto-fill behavior used on the Compose page.
  const { data: repDefaults } = useGetRepDefaults();
  const existingUserSlugs = useMemo(
    () => (userTemplatesQuery.data ?? []).map((t) => t.slug),
    [userTemplatesQuery.data],
  );

  const [bodyHtml, setBodyHtml] = useState("");
  const [subject, setSubject] = useState("");
  const [previewText, setPreviewText] = useState("");
  const [testVariables, setTestVariables] = useState<Record<string, string>>({});
  const [isRenaming, setIsRenaming] = useState(false);
  const [nameDraft, setNameDraft] = useState("");
  const [snippetPickerSlug, setSnippetPickerSlug] = useState<string>("");
  const bodyTextareaRef = useRef<HTMLTextAreaElement | null>(null);

  const snippetsQuery = useListSnippets();
  const usedSnippetsQuery = useListTemplateUsedSnippets(templateId, {
    query: { enabled: !!templateId, queryKey: getListTemplateUsedSnippetsQueryKey(templateId) },
  });
  // Per-template engagement aggregates surfaced as a stat panel + 30-day
  // trend chart in the editor sidebar. The endpoint scopes to the current
  // user's `live` sends only and excludes bot events, mirroring the
  // dashboard's engagement aggregation. Built-ins return zeros for new
  // workspaces (no sends from this user yet) — the panel renders an
  // empty state in that case rather than hiding itself, so users
  // discover the feature even before they've sent anything.
  const engagementQuery = useGetTemplateEngagement(templateId, {
    query: {
      enabled: !!templateId,
      queryKey: getGetTemplateEngagementQueryKey(templateId),
    },
  });
  const [dismissedSlugNotices, setDismissedSlugNotices] = useState<Set<string>>(
    () => readDismissedSlugNotices(),
  );

  const renameSlugAdjustment = useMemo(
    () =>
      template
        ? predictSlugAdjustment(nameDraft, existingUserSlugs, template.slug)
        : null,
    [nameDraft, existingUserSlugs, template],
  );

  const dismissSlugNotice = () => {
    if (!template) return;
    const next = new Set(dismissedSlugNotices);
    next.add(String(template.id));
    setDismissedSlugNotices(next);
    persistDismissedSlugNotices(next);
  };
  
  // The iframe's HTML is owned by `MultiClientPreview` (it fetches
  // post-processed HTML per client profile). We still keep
  // `renderedSubject` and `missingVars` here because they're surfaced
  // outside the preview frame (header chrome + missing-vars badge).
  const [renderedSubject, setRenderedSubject] = useState("");
  const [missingVars, setMissingVars] = useState<string[]>([]);
  // Surfaces failures of the shell render call (subject + missing-vars
  // metadata). Without this, a 5xx/network failure would silently leave
  // the preview chrome stale and the iframe blank with no explanation.
  // `renderShellRetryNonce` lets the inline "Try again" button re-trigger
  // the render effect.
  const [renderShellError, setRenderShellError] = useState<string | null>(null);
  const [renderShellRetryNonce, setRenderShellRetryNonce] = useState(0);

  // Template-load effect: seed the EDITABLE template fields (body /
  // subject / preview text) only when the template itself changes.
  // Deliberately NOT keyed on `repDefaults` — otherwise rep defaults
  // arriving after the user starts editing would silently wipe their
  // unsaved changes.
  useEffect(() => {
    if (template) {
      setBodyHtml(template.bodyHtml);
      setSubject(template.subject);
      setPreviewText(template.previewText || "");
    }
  }, [template]);

  // Test-variables effect: seeds the live-preview variable inputs.
  // Same rep-default fallback map used in Compose — the saved
  // Representative name auto-fills `repName`/`agentName` so the
  // preview reflects what the user actually sends, not the seeded
  // sample (e.g. "Alex Rivera"). Re-runs when `repDefaults` resolves
  // so the preview catches up; safe because this only updates
  // `testVariables`, not the editable template body.
  useEffect(() => {
    if (template) {
      const repFallbacks: Record<string, string | null | undefined> = {
        repName: repDefaults?.repName,
        agentName: repDefaults?.repName,
        agentTitle: repDefaults?.agentTitle,
        repPhone: repDefaults?.repPhone,
        employeeId: repDefaults?.employeeId,
        caseId: repDefaults?.caseId,
      };
      const initialVars: Record<string, string> = {};
      template.variables.forEach(v => {
        const repVal = repFallbacks[v.key];
        if (typeof repVal === "string" && repVal !== "") {
          initialVars[v.key] = repVal;
        } else {
          initialVars[v.key] = v.defaultValue || `[${v.label}]`;
        }
      });
      setTestVariables(initialVars);
    }
  }, [template, repDefaults]);

  const debouncedSubject = useDebounce(subject, 250);
  const debouncedVars = useDebounce(testVariables, 250);

  useEffect(() => {
    if (!template) return;
    let isCancelled = false;

    const render = async () => {
      try {
        const res = await renderMutation.mutateAsync({
          id: templateId,
          data: {
            variables: debouncedVars,
            subjectOverride: debouncedSubject !== template.subject ? debouncedSubject : undefined,
          }
        });
        if (!isCancelled) {
          setRenderedSubject(res.subject);
          setMissingVars(res.missingVariables);
          setRenderShellError(null);
        }
      } catch (err: unknown) {
        if (!isCancelled) {
          // Fallback is a bare reason ("Unknown error") rather than
          // "Couldn't render preview." because the strip in
          // MultiClientPreview already prefixes with
          // "Couldn't render preview — …".
          setRenderShellError(apiErrorMessage(err, "Unknown error"));
        }
      }
    };
    render();
    return () => { isCancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [template, templateId, debouncedSubject, debouncedVars, renderShellRetryNonce]);

  const startRename = () => {
    if (!template) return;
    setNameDraft(template.name);
    setIsRenaming(true);
  };

  const cancelRename = () => {
    setIsRenaming(false);
    setNameDraft("");
  };

  const handleRename = async () => {
    if (!template) return;
    const trimmed = nameDraft.trim();
    if (!trimmed) {
      toast.error("Name is required");
      return;
    }
    if (trimmed === template.name) {
      cancelRename();
      return;
    }
    try {
      await renameMutation.mutateAsync({
        id: templateId,
        data: { name: trimmed },
      });
      toast.success("Template renamed");
      queryClient.invalidateQueries({ queryKey: getGetTemplateQueryKey(templateId) });
      queryClient.invalidateQueries({ queryKey: getListTemplatesQueryKey().slice(0, 1) });
      setIsRenaming(false);
    } catch (e: unknown) {
      toast.error(apiErrorMessage(e, "Failed to rename template"));
    }
  };

  const handleSave = async () => {
    if (!template) return;
    try {
      await updateMutation.mutateAsync({
        id: templateId,
        data: {
          subject,
          previewText: previewText || null,
          bodyHtml,
        }
      });
      toast.success("Template saved");
      queryClient.invalidateQueries({ queryKey: getGetTemplateQueryKey(templateId) });
      queryClient.invalidateQueries({ queryKey: getListTemplatesQueryKey() });
      queryClient.invalidateQueries({ queryKey: getListTemplateUsedSnippetsQueryKey(templateId) });

      // Force re-render with the just-saved html so the missing-vars
      // banner / subject header in the chrome catches up immediately.
      // The MultiClientPreview component re-fetches per-client HTML
      // on its own when its inputs change.
      try {
        const res = await renderMutation.mutateAsync({
          id: templateId,
          data: { variables: testVariables }
        });
        setRenderedSubject(res.subject);
        setMissingVars(res.missingVariables);
        setRenderShellError(null);
      } catch (renderErr: unknown) {
        setRenderShellError(apiErrorMessage(renderErr, "Unknown error"));
      }

    } catch (e: unknown) {
      toast.error(apiErrorMessage(e, "Failed to save template"));
    }
  };

  const insertSnippetAtCursor = (slug: string) => {
    if (!slug) return;
    const snippet = `{{> ${slug}}}`;
    const ta = bodyTextareaRef.current;
    if (!ta) {
      setBodyHtml((prev) => `${prev}${prev && !prev.endsWith("\n") ? "\n" : ""}${snippet}`);
      return;
    }
    const start = ta.selectionStart ?? bodyHtml.length;
    const end = ta.selectionEnd ?? bodyHtml.length;
    const next = bodyHtml.slice(0, start) + snippet + bodyHtml.slice(end);
    setBodyHtml(next);
    requestAnimationFrame(() => {
      const t = bodyTextareaRef.current;
      if (!t) return;
      const cursor = start + snippet.length;
      t.focus();
      t.setSelectionRange(cursor, cursor);
    });
  };

  if (isLoading || !template) {
    return <div className="flex-1 flex justify-center items-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div>;
  }

  const isReadonly = template.isBuiltin;
  const showSlugNotice =
    !template.isBuiltin &&
    isAutoSuffixedSlug(template.name, template.slug) &&
    !isSlugNoticeDismissedForTemplate(dismissedSlugNotices, template.id);

  return (
    <div className="flex-1 flex flex-col h-[calc(100vh-64px)] md:h-screen overflow-hidden bg-background">
      <div className="min-h-16 px-6 py-3 border-b border-border flex items-center justify-between shrink-0 bg-card gap-4">
        <div className="flex items-center gap-4 flex-1 min-w-0">
          <Button variant="ghost" size="icon" asChild>
            <Link href="/app/templates"><ArrowLeft className="w-5 h-5" /></Link>
          </Button>
          <div className="flex-1 min-w-0">
            {isRenaming && !isReadonly ? (
              <div className="flex flex-col gap-1.5">
                <div className="flex items-center gap-2">
                  <Input
                    autoFocus
                    value={nameDraft}
                    onChange={(e) => setNameDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        handleRename();
                      } else if (e.key === "Escape") {
                        e.preventDefault();
                        cancelRename();
                      }
                    }}
                    className="h-9 max-w-md font-bold text-lg"
                    aria-label="Template name"
                    data-testid="input-rename-template"
                    disabled={renameMutation.isPending}
                  />
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8"
                    onClick={handleRename}
                    disabled={renameMutation.isPending}
                    aria-label="Save name"
                    data-testid="button-rename-save"
                  >
                    {renameMutation.isPending ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Check className="w-4 h-4" />
                    )}
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-8 w-8"
                    onClick={cancelRename}
                    disabled={renameMutation.isPending}
                    aria-label="Cancel rename"
                    data-testid="button-rename-cancel"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
                {renameSlugAdjustment && (
                  <p
                    className="text-xs text-blue-700 dark:text-blue-300"
                    data-testid="rename-slug-adjustment-hint"
                  >
                    {renameSlugAdjustment.reason === "reserved"
                      ? "This name is reserved — your URL will be adjusted to "
                      : "You already have a template with this name — your URL will be adjusted to "}
                    <code className="font-mono text-[11px] bg-blue-500/15 px-1 py-0.5 rounded">
                      {renameSlugAdjustment.baseSlug}-…
                    </code>
                    {" "}so it stays unique. The template name itself is unchanged.
                  </p>
                )}
              </div>
            ) : (
              <h1 className="font-bold text-lg flex items-center gap-2 min-w-0">
                <span className="truncate">{template.name}</span>
                {isReadonly ? (
                  <Badge variant="secondary" className="text-[10px] shrink-0">Built-in / Read-only</Badge>
                ) : (
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    className="h-7 w-7 text-muted-foreground hover:text-foreground shrink-0"
                    onClick={startRename}
                    aria-label="Rename template"
                    data-testid="button-rename-template"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                  </Button>
                )}
              </h1>
            )}
          </div>
        </div>
        <div className="flex items-center gap-3">
          {!isReadonly && (
            <Button variant="outline" onClick={handleSave} disabled={updateMutation.isPending}>
              {updateMutation.isPending ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Changes
            </Button>
          )}
          <Button asChild>
            <Link href={`/app/compose?templateId=${template.id}`}>
              <Send className="w-4 h-4 mr-2" />
              Compose
            </Link>
          </Button>
        </div>
      </div>

      <div className="flex-1 flex flex-col lg:flex-row overflow-hidden">
        {/* Editor Side */}
        <div className="w-full lg:w-1/2 flex flex-col border-r border-border bg-muted/10 overflow-auto">
          <div className="p-6 space-y-6">
            {showSlugNotice && (
              <div
                className="flex items-start gap-3 rounded-lg border border-blue-500/30 bg-blue-500/10 px-4 py-3 text-sm text-blue-900 dark:text-blue-200"
                data-testid="slug-auto-suffix-notice"
              >
                <Info className="w-4 h-4 mt-0.5 shrink-0" />
                <div className="flex-1">
                  <p className="font-medium">URL was adjusted to avoid a conflict.</p>
                  <p className="text-xs opacity-90 mt-0.5">
                    Your template's URL ends in{" "}
                    <code className="font-mono text-[11px] bg-blue-500/15 px-1 py-0.5 rounded">
                      {template.slug}
                    </code>{" "}
                    because the name you chose is reserved or already in use.
                    The template name itself is unchanged.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  className="h-6 w-6 -mr-1 -mt-1 text-blue-900/70 hover:text-blue-900 dark:text-blue-200/70 dark:hover:text-blue-200"
                  onClick={dismissSlugNotice}
                  aria-label="Dismiss URL adjustment notice"
                  data-testid="slug-auto-suffix-notice-dismiss"
                >
                  <X className="w-3.5 h-3.5" />
                </Button>
              </div>
            )}
            <TemplateEngagementPanel
              isLoading={engagementQuery.isLoading}
              error={engagementQuery.error ? apiErrorMessage(engagementQuery.error, "Failed to load engagement metrics") : null}
              engagement={engagementQuery.data ?? null}
            />
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">Subject</label>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} disabled={isReadonly} />
              </div>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">Preview Text</label>
                <Input value={previewText} onChange={(e) => setPreviewText(e.target.value)} disabled={isReadonly} />
              </div>
              {!isReadonly && (
                <div>
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block flex justify-between">
                    <span>HTML Body</span>
                    <span className="text-[10px] lowercase normal-case opacity-70">Must save to see preview changes</span>
                  </label>
                  <Textarea
                    ref={bodyTextareaRef}
                    value={bodyHtml}
                    onChange={(e) => setBodyHtml(e.target.value)}
                    rows={15}
                    className="font-mono text-xs"
                    data-testid="textarea-body-html"
                  />
                </div>
              )}

              {!isReadonly && (
                <div data-testid="snippet-picker">
                  <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block flex items-center gap-1.5">
                    <Puzzle className="w-3 h-3" />
                    <span>Insert Snippet</span>
                  </label>
                  <div className="flex items-center gap-2">
                    <Select
                      value={snippetPickerSlug}
                      onValueChange={(v) => {
                        setSnippetPickerSlug(v);
                        insertSnippetAtCursor(v);
                        setSnippetPickerSlug("");
                      }}
                    >
                      <SelectTrigger
                        className="h-8 text-xs flex-1"
                        aria-label="Insert snippet"
                        data-testid="select-insert-snippet"
                      >
                        <SelectValue placeholder="Pick a snippet to insert…" />
                      </SelectTrigger>
                      <SelectContent>
                        {(snippetsQuery.data ?? []).length === 0 ? (
                          <div className="px-2 py-1.5 text-xs text-muted-foreground">No snippets yet</div>
                        ) : (
                          (snippetsQuery.data ?? []).map((s) => (
                            <SelectItem
                              key={s.id}
                              value={s.slug}
                              data-testid={`option-snippet-${s.slug}`}
                            >
                              <span className="truncate">{s.name}</span>
                              {s.isBuiltin && (
                                <span className="ml-2 text-[10px] text-muted-foreground">built-in</span>
                              )}
                              <code className="ml-2 text-[10px] text-muted-foreground">{s.slug}</code>
                            </SelectItem>
                          ))
                        )}
                      </SelectContent>
                    </Select>
                    <Button asChild variant="ghost" size="sm" className="h-8 text-xs">
                      <Link href="/app/snippets">Manage</Link>
                    </Button>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    Inserts <code>{`{{> slug}}`}</code> at the cursor position. Save to see it in the preview.
                  </p>
                </div>
              )}

              <div data-testid="used-snippets-panel">
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5 block">
                  Used Snippets
                </label>
                {usedSnippetsQuery.isLoading ? (
                  <div className="text-xs text-muted-foreground flex items-center gap-2">
                    <Loader2 className="w-3 h-3 animate-spin" /> Loading…
                  </div>
                ) : (usedSnippetsQuery.data ?? []).length === 0 ? (
                  <p className="text-xs text-muted-foreground">This template doesn't reference any snippets yet.</p>
                ) : (
                  <ul className="space-y-1.5">
                    {(usedSnippetsQuery.data ?? []).map((s) => (
                      <li
                        key={s.id}
                        className="flex items-center justify-between gap-2 text-xs rounded border border-border bg-card px-2 py-1.5"
                        data-testid={`used-snippet-${s.slug}`}
                      >
                        <div className="flex items-center gap-2 min-w-0">
                          <Puzzle className="w-3 h-3 text-muted-foreground shrink-0" />
                          <span className="truncate font-medium">{s.name}</span>
                          {s.isBuiltin && (
                            <Badge variant="secondary" className="text-[9px] py-0 px-1.5 h-4">built-in</Badge>
                          )}
                          <code className="text-[10px] text-muted-foreground truncate">{s.slug}</code>
                        </div>
                        <Link
                          href={`/app/snippets/${s.id}`}
                          className="text-[11px] text-primary hover:underline shrink-0"
                        >
                          View
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="border-t border-border pt-6">
              <h3 className="text-sm font-semibold mb-4">Test Variables</h3>
              {template.variables.length === 0 ? (
                <p className="text-xs text-muted-foreground">This template has no variables.</p>
              ) : (
                <div className="space-y-3">
                  {template.variables.map(v => (
                    <div key={v.key}>
                      <label className="text-xs font-medium mb-1 block">{v.label} <code className="text-[10px] text-muted-foreground ml-1">{`{{${v.key}}}`}</code></label>
                      <Input 
                        value={testVariables[v.key] || ""} 
                        onChange={(e) => setTestVariables(prev => ({ ...prev, [v.key]: e.target.value }))} 
                        className="h-8 text-sm bg-background"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Preview Side — multi-client tabbed preview (Gmail Web / Mobile,
            Apple Mail, Outlook Desktop / Web) plus a per-client
            compatibility report. */}
        <div className="w-full lg:w-1/2 flex">
          <MultiClientPreview
            templateId={templateId}
            variables={debouncedVars}
            subjectOverride={
              debouncedSubject !== template.subject
                ? debouncedSubject
                : undefined
            }
            displaySubject={renderedSubject || template.subject}
            missingVariables={missingVars}
            shellError={renderShellError}
            onRetryShell={() => setRenderShellRetryNonce((n) => n + 1)}
          />
        </div>
      </div>
    </div>
  );
}

// Format a `YYYY-MM-DD` UTC date as a short localized label (e.g. "Apr
// 23"). Anchors at noon UTC so DST transitions don't push the label
// onto the previous local day. Mirrors the dashboard's `formatDayLabel`
// — duplicated here rather than shared so the template-detail page
// doesn't take a dependency on the dashboard module.
function formatDayLabel(isoDate: string): string {
  const [y, m, d] = isoDate.split("-").map((n) => Number(n));
  const dt = new Date(Date.UTC(y, m - 1, d, 12));
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function formatPct(rate: number): string {
  return `${(rate * 100).toFixed(1)}%`;
}

// Per-template engagement panel rendered inside the editor sidebar.
// Three stat tiles (Lifetime sends, Open rate, Click rate) plus a
// 30-day opens-vs-clicks trend sparkline. Renders zero-state copy when
// the template has no live sends yet, an error banner on fetch
// failure, and a skeleton while the initial query is in-flight.
function TemplateEngagementPanel({
  isLoading,
  error,
  engagement,
}: {
  isLoading: boolean;
  error: string | null;
  engagement: TemplateEngagement | null;
}) {
  if (isLoading) {
    return (
      <Card data-testid="template-engagement-loading">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <BarChart3 className="w-4 h-4" /> Engagement
          </CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-muted-foreground flex items-center gap-2">
          <Loader2 className="w-3 h-3 animate-spin" /> Loading engagement…
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card data-testid="template-engagement-error">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <BarChart3 className="w-4 h-4" /> Engagement
          </CardTitle>
        </CardHeader>
        <CardContent className="text-xs text-destructive">{error}</CardContent>
      </Card>
    );
  }

  if (!engagement) return null;

  const lifetimeSendsLabel = engagement.lifetimeSends === 1 ? "send" : "sends";
  const trendData = engagement.daily30.map((d) => ({
    day: formatDayLabel(d.date),
    Opens: d.opens,
    Clicks: d.clicks,
  }));
  const trendMax = engagement.daily30.reduce(
    (m, d) => Math.max(m, d.opens, d.clicks),
    0,
  );
  const hasLifetimeSends = engagement.lifetimeSends > 0;

  return (
    <div className="space-y-3" data-testid="template-engagement">
      <div className="grid grid-cols-3 gap-2">
        <Card data-testid="template-engagement-sends">
          <CardHeader className="pb-1 px-3 pt-3">
            <CardTitle className="text-[11px] font-medium text-muted-foreground flex items-center justify-between">
              <span>Lifetime sends</span>
              <Send className="h-3 w-3" />
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3">
            <div className="text-xl font-bold">
              {engagement.lifetimeSends.toLocaleString()}
            </div>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              {engagement.sent30Days.toLocaleString()} in last 30d
            </p>
          </CardContent>
        </Card>
        <Card data-testid="template-engagement-open-rate">
          <CardHeader className="pb-1 px-3 pt-3">
            <CardTitle className="text-[11px] font-medium text-muted-foreground flex items-center justify-between">
              <span>Open rate</span>
              <Eye className="h-3 w-3" />
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3">
            <div className="text-xl font-bold">
              {hasLifetimeSends ? formatPct(engagement.lifetimeOpenRate) : "—"}
            </div>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              {engagement.uniqueLifetimeOpens.toLocaleString()} of{" "}
              {engagement.lifetimeSends.toLocaleString()} {lifetimeSendsLabel}
            </p>
          </CardContent>
        </Card>
        <Card data-testid="template-engagement-click-rate">
          <CardHeader className="pb-1 px-3 pt-3">
            <CardTitle className="text-[11px] font-medium text-muted-foreground flex items-center justify-between">
              <span>Click rate</span>
              <MousePointerClick className="h-3 w-3" />
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 pb-3">
            <div className="text-xl font-bold">
              {hasLifetimeSends ? formatPct(engagement.lifetimeClickRate) : "—"}
            </div>
            <p className="text-[10px] text-muted-foreground mt-0.5">
              {engagement.uniqueLifetimeClicks.toLocaleString()} of{" "}
              {engagement.lifetimeSends.toLocaleString()} {lifetimeSendsLabel}
            </p>
          </CardContent>
        </Card>
      </div>
      <Card data-testid="template-engagement-trend">
        <CardHeader className="pb-1 px-3 pt-3">
          <CardTitle className="text-[11px] font-medium flex items-center gap-1.5">
            <BarChart3 className="w-3 h-3 text-muted-foreground" />
            <span>30-day trend</span>
          </CardTitle>
          <CardDescription className="text-[10px]">
            Opens vs clicks per day, last 30 days. Bot events excluded.
          </CardDescription>
        </CardHeader>
        <CardContent className="px-3 pb-3">
          {trendMax === 0 ? (
            <div className="text-[11px] text-muted-foreground text-center py-4">
              {hasLifetimeSends
                ? "No engagement events in the last 30 days yet."
                : "Send this template to start tracking opens and clicks."}
            </div>
          ) : (
            <div className="h-24 w-full">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart
                  data={trendData}
                  margin={{ top: 4, right: 4, bottom: 0, left: -28 }}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="hsl(var(--border))"
                    vertical={false}
                  />
                  <XAxis
                    dataKey="day"
                    tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }}
                    axisLine={false}
                    tickLine={false}
                    interval="preserveStartEnd"
                  />
                  <YAxis
                    allowDecimals={false}
                    tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }}
                    axisLine={false}
                    tickLine={false}
                    width={28}
                  />
                  <RTooltip
                    cursor={{ stroke: "hsl(var(--muted))", opacity: 0.4 }}
                    contentStyle={{
                      background: "hsl(var(--popover))",
                      border: "1px solid hsl(var(--border))",
                      borderRadius: "6px",
                      fontSize: "11px",
                    }}
                  />
                  <Line
                    type="monotone"
                    dataKey="Opens"
                    stroke="hsl(221 83% 53%)"
                    strokeWidth={2}
                    dot={false}
                  />
                  <Line
                    type="monotone"
                    dataKey="Clicks"
                    stroke="hsl(142 71% 45%)"
                    strokeWidth={2}
                    dot={false}
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
